import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { SelectOption } from './types';

const filterOptions = createFilterOptions<SelectOption>();

type SelectFieldProps = {
  value: string;
  options: SelectOption[];
  placeholder?: string;
  listHeader?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

const SelectField = ({
  value,
  options,
  placeholder,
  listHeader,
  onChange,
  disabled,
}: SelectFieldProps) => {
  const resolvedValue = value
    ? options.find((option) => option.value === value) ?? {
        value,
        label: value,
      }
    : null;
  const headerLabel = listHeader?.trim() ?? '';

  return (
    <Autocomplete
      freeSolo
      options={options}
      value={resolvedValue}
      inputValue={value}
      disabled={disabled}
      groupBy={headerLabel ? () => headerLabel : undefined}
      isOptionEqualToValue={(option, current) => option.value === current.value}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.label
      }
      filterOptions={(items, params) => {
        const filtered = filterOptions(items, params);
        const inputValue = params.inputValue.trim();
        const exists = items.some(
          (item) => item.label.toLowerCase() === inputValue.toLowerCase()
        );
        if (inputValue && !exists) {
          filtered.push({ value: inputValue, label: inputValue });
        }
        return filtered;
      }}
      onInputChange={(_, newInputValue) => onChange(newInputValue)}
      onChange={(_, newValue) => {
        if (typeof newValue === 'string') {
          onChange(newValue);
          return;
        }
        if (newValue?.value) {
          onChange(newValue.value);
          return;
        }
        onChange('');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: '#fff',
              minHeight: '44px',
              paddingRight: '8px',
            },
            '& .MuiOutlinedInput-input': {
              padding: '10px 14px',
              fontSize: '14px',
              color: '#475569',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: '#94A3B8',
              opacity: 1,
            },
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: '#E2E8F0',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
              {
                borderColor: '#2563EB',
              },
            '& .MuiSvgIcon-root': {
              color: '#94A3B8',
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          {option.label}
        </li>
      )}
      slotProps={{
        popper: {
          sx: {
            '&[data-popper-placement*="bottom"] .MuiAutocomplete-paper': {
              marginTop: '8px',
            },
            '&[data-popper-placement*="top"] .MuiAutocomplete-paper': {
              marginBottom: '8px',
            },
          },
        },
      }}
      sx={{
        '& .MuiAutocomplete-paper': {
          borderRadius: '10px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 16px 32px -20px rgba(15, 23, 42, 0.4)',
          overflow: 'hidden',
        },
        '& .MuiAutocomplete-listbox': {
          padding: 0,
        },
        '& .MuiAutocomplete-groupLabel': {
          padding: '12px 16px',
          fontSize: '13px',
          fontWeight: 500,
          color: '#94A3B8',
          backgroundColor: '#fff',
          borderBottom: '1px solid #F1F5F9',
        },
        '& .MuiAutocomplete-groupUl': {
          padding: 0,
          margin: 0,
        },
        '& .MuiAutocomplete-option': {
          padding: '14px 16px',
          fontSize: '14px',
          color: '#64748B',
          borderBottom: '1px solid #F1F5F9',
          '&[aria-selected="true"]': {
            backgroundColor: '#F1F5F9',
          },
          '&.Mui-focused': {
            backgroundColor: '#F8FAFC',
          },
          '&:last-of-type': {
            borderBottom: 'none',
          },
        },
      }}
    />
  );
};

export default SelectField;
